import  { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown } from "lucide-react";

/** ---- Minimal local Typewriter (unchanged) ---- */
const Typewriter = ({
  words = [],
  loop = true,
  typeSpeed = 75,
  deleteSpeed = 50,
  cursor = "_",
}) => {
  const [wordIndex, setWordIndex] = useState(0);
  const [display, setDisplay] = useState("");
  const [deleting, setDeleting] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    const current = words[wordIndex] || "";
    const isDone = !loop && wordIndex >= words.length;
    if (isDone) return;

    const tick = () => {
      const nextLen = deleting ? display.length - 1 : display.length + 1;
      const nextText = current.slice(0, Math.max(0, Math.min(current.length, nextLen)));
      setDisplay(nextText);

      if (!deleting && nextText === current) {
        setTimeout(() => setDeleting(true), 700);
      } else if (deleting && nextText === "") {
        setDeleting(false);
        setWordIndex((i) => (i + 1) % (words.length || 1));
      }
    };

    const delay = deleting ? deleteSpeed : typeSpeed;
    timerRef.current = setTimeout(tick, delay);
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, [display, deleting, wordIndex, words, loop, typeSpeed, deleteSpeed]);

  useEffect(() => {
    setDisplay("");
    setDeleting(false);
  }, [wordIndex]);

  return (
    <span>
      {display}
      <span className="ml-1 opacity-80">{cursor}</span>
    </span>
  );
};

/** ---- Content for both variants (same UI, different copy/data) ---- */
const CONTENT = {
  jobsuite: {
    suiteName: "Aspirant Suite",
    heroWords: ["{Career Path}", "{Resume}"],
    heroSub:
      "Combine human insight with AI precision to craft your career story, practice with confidence, and achieve your professional goals.",
    ctaLink: "https://job.novistep.com/auth",
    featuresIntroTitle: "Our Core Features",
    featuresIntroSub:
      "Built for modern jobseekers and teams. Each feature is backed by explainable AI and delivers tangible career outcomes.",
    features: [
      {
        id: 1,
        title: "AI Resume Scoring",
        desc: "Optimize your resume with AI-driven scoring and feedback. Generate tailored bullet points, quantify impact, and export ATS-friendly versions.",
        img: require("../img/cat-resume.png"),
        color: "bg-purple-200",
      },
      {
        id: 2,
        title: "AI Job Matching",
        desc: "Get personalized job recommendations with fit scores and why-you-match explanations.",
        img: require("../img/cat-jobmatching.png"),
        color: "bg-yellow-200",
      },
      {
        id: 3,
        title: "Career Guidance",
        desc: "Receive roadmaps with stages, courses, and weekly milestones aligned to your target role.",
        img: require("../img/cat-career.png"),
        color: "bg-purple-200",
      },
      {
        id: 4,
        title: "Interview Simulation",
        desc: "Practice with an AI interviewer; get feedback on answers, structure, and delivery.",
        img: require("../img/cat-laptop.png"),
        color: "bg-yellow-200",
      },
      {
        id: 5,
        title: "Cover Letter Generator",
        desc: "Create tailored cover letters in seconds based on JD analysis.",
        img: require("../img/cat-coverletter.png"),
        color: "bg-purple-200",
      },
      {
        id: 6,
        title: "Continuous Learning",
        desc: "Ongoing micro-tasks, skills refinement, and progress dashboards informed by your activity.",
        img: require("../img/cat-learning.png"),
        color: "bg-yellow-200",
      },
    ],
    testimonialsTitle: "Trusted by Professionals Worldwide",
    testimonialsSub:
      "Companies and individuals rely on Aspirant Suite to scale hiring, mentoring, and upskilling.",
    testimonials: [
      {
        name: "Cian Murphy",
        review:
          "A Game-Changer for My First Job Search. The AI Resume Builder and interview simulation boosted my confidence and results.",
        img: require("../img/user1.jpg"),
        role: "Computer Science Student",
        badge: "300+ resumes optimized",
      },
      {
        name: "Nolan Walsh",
        review:
          "Finally, a smarter job hunt. Resume scoring and automated matching helped me break ATS walls.",
        img: require("../img/user2.jpg"),
        role: "Early-Career Job Hunter",
        badge: "800+ interviews aced",
      },
      {
        name: "Priya Sharma",
        review:
          "More than a job finder—it's a career planner. The roadmap clarified my long-term growth.",
        img: require("../img/user3.jpg"),
        role: "Young Professional",
        badge: "500+ jobs matched",
      },
    ],
    faqsTitle: "Frequently Asked Questions",
    faqsRightTitle: "Do you have more questions?",
    faqsRightText:
      "Reach out to us — we’ll help you with personalized solutions for your career growth.",
    faqsMail: "mailto:info@novistep.com",
    faqs: [
      {
        id: 1,
        question: "What is the Aspirant Suite?",
        answer:
          "An AI-powered platform for early-career professionals covering resumes, interviews, matching, and career planning.",
      },
      {
        id: 2,
        question: "How does AI Resume Scoring work?",
        answer:
          "It benchmarks your resume against the JD and industry patterns, then returns a score with actionable edits.",
      },
      {
        id: 3,
        question: "Is the interview practice realistic?",
        answer:
          "Yes—text and speech modes with feedback on structure, content, pace, and filler words.",
      },
      {
        id: 4,
        question: "How are jobs recommended?",
        answer:
          "Our recommender matches skills, history, and interests to roles, highlighting gaps and quick wins.",
      },
    ],
  },
  studysuite: {
    suiteName: "Study Suite",
    heroWords: ["{Study Plan}", "{SOP}"],
    heroSub:
      "Use AI to plan your study abroad journey—discover courses, prepare for visa/university interviews, and craft winning documents.",
    ctaLink: "https://study.novistep.com",
    featuresIntroTitle: "Study Abroad — AI Core Features",
    featuresIntroSub:
      "Built for international applicants. Every feature is guided by transparent reasoning to improve outcomes.",
    features: [
      {
        id: 1,
        title: "AI Course & University Suggestion",
        desc: "Get tailored programs by country, budget, background, and goals with clear rationale and competitiveness scores.",
        img: require("../img/cat-career.png"),
        color: "bg-purple-200",
      },
      {
        id: 2,
        title: "AI Visa & University Mock Interviews",
        desc: "Practice realistic interviews (speech/text) and receive feedback on intent, finances, ties, and clarity.",
        img: require("../img/cat-laptop.png"),
        color: "bg-yellow-200",
      },
      {
        id: 3,
        title: "SOP / LOR / CV / Cover Builder",
        desc: "Generate, iterate, and polish documents with prompts, structure checks, plagiarism-safe rewrites, and export.",
        img: require("../img/cat-coverletter.png"),
        color: "bg-purple-200",
      },
      {
        id: 4,
        title: "Scholarship & Intake Guidance",
        desc: "Discover scholarships and intakes with eligibility mapping and deadline reminders.",
        img: require("../img/cat-jobmatching.png"),
        color: "bg-yellow-200",
      },
      {
        id: 5,
        title: "Application Tracker",
        desc: "Track applications, deadlines, fees, and document status in one unified dashboard.",
        img: require("../img/cat-resume.png"),
        color: "bg-purple-200",
      },
      {
        id: 6,
        title: "Document Quality & Compliance",
        desc: "Multi-check reviews for clarity, tone, word limits, originality, and university/visa requirements.",
        img: require("../img/cat-learning.png"),
        color: "bg-yellow-200",
      },
    ],
    testimonialsTitle: "Loved by Study Abroad Applicants",
    testimonialsSub:
      "Applicants use Study Suite to pick the right course, ace interviews, and submit standout applications.",
    testimonials: [
      {
        name: "Meera Kapoor",
        review:
          "Course suggestions were on point with my budget and profile. The SOP builder made my narrative crisp and authentic.",
        img: require("../img/user5.jpg"),
        role: "MS Applicant — Germany",
        badge: "SOP finalized in 2 days",
      },
      {
        name: "Daniel Park",
        review:
          "Visa mock interviews highlighted weak spots in finances/ties. The feedback helped me answer confidently.",
        img: require("../img/user6.jpg"),
        role: "MBA Applicant — Canada",
        badge: "Visa interview cleared",
      },
      {
        name: "Aisha Khan",
        review:
          "The tracker and scholarship guidance saved me from missing deadlines. Total lifesaver.",
        img: require("../img/user4.jpg"),
        role: "BS Applicant — UK",
        badge: "4 admits, 1 scholarship",
      },
    ],
    faqsTitle: "Study Suite — FAQs",
    faqsRightTitle: "Need help with your profile?",
    faqsRightText:
      "Email us for a personalized plan—country, program, timeline, and scholarships.",
    faqsMail: "mailto:hello@studysuite.ai",
    faqs: [
      {
        id: 1,
        question: "Who is Study Suite for?",
        answer:
          "For students and professionals applying abroad who want AI help with programs, documents, and interviews.",
      },
      {
        id: 2,
        question: "How does AI pick courses and universities?",
        answer:
          "It uses your academics, budget, preferences, and goals to rank options with an explainable score.",
      },
      {
        id: 3,
        question: "Are visa/university mocks realistic?",
        answer:
          "Yes—speech/text simulations with feedback on intent, ties, finances, and answer structure.",
      },
      {
        id: 4,
        question: "Will my SOP/LOR be unique?",
        answer:
          "We guide structure and language while preserving your voice, and run originality and tone checks.",
      },
      {
        id: 5,
        question: "Can I track deadlines and intakes?",
        answer:
          "The tracker monitors deadlines, fees, and documents. You can also set reminders per university.",
      },
    ],
  },
};

/** 
 * Desktop20
 * @param {Object} props
 * @param {"jobsuite"|"studysuite"} props.suite - content variant (defaults to "jobsuite")
 */
const Desktop20 = ({ suite = "jobsuite" }) => {
  const navigate = useNavigate();
  const [openFeature, setOpenFeature] = useState(null);
  const [openFAQ, setOpenFAQ] = useState(null);

  // Guard: fallback to jobsuite if unknown value
  const S = CONTENT[suite] || CONTENT.jobsuite;

  return (
    <div className="bg-[#06010a] text-white font-poppins overflow-x-hidden">
      {/* Hero Section */}
      <section className="flex flex-col justify-center items-center text-center min-h-screen px-4 bg-gradient-to-b from-[#06010a] to-[#260345]">
        <h1 className="text-4xl md:text-6xl font-bold mb-4 text-white">
          Let AI refine your{" "}
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-purple-600 font-extrabold ">
            <Typewriter
              words={S.heroWords}
              loop={true}
              typeSpeed={85}
              deleteSpeed={70}
              cursor={"_"}
              cursorSpeed={1000}
            />
          </span>
        </h1>

        <p className="text-lg text-gray-300 mb-8 max-w-2xl">{S.heroSub}</p>

        <img
          src={require("../img/mascot.png")}
          alt="Mascot"
          className="w-52 mb-5 animate-bounce"
          style={{ animationDuration: "2.5s" }}
        />
        
        {/* Dynamic Get Started Button */}
        <button
          onClick={() => {
             window.location.href = S.ctaLink;
          }}
          className="bg-[#ffc403] text-black font-bold px-6 py-3 rounded-xl hover:bg-[#e6b800] transition"
        >
          Get Started Free
        </button>

      </section>

      {/* Features Section */}
      <section className="bg-white text-black py-20 px-8" id="features">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
          {S.featuresIntroTitle}
        </h2>
        <p className="text-center text-gray-600 max-w-2xl mx-auto mb-12">
          {S.featuresIntroSub}
        </p>
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {S.features.map((feature) => (
            <div
              key={feature.id}
              className={`p-6 rounded-2xl shadow-md text-center cursor-pointer transition transform hover:scale-105 ${feature.color}`}
              onClick={() => setOpenFeature(openFeature === feature.id ? null : feature.id)}
            >
              <img src={feature.img} alt={feature.title} className="w-24 mx-auto mb-4" />
              <h3 className="font-semibold text-lg mb-2">{feature.title}</h3>
              {openFeature === feature.id && (
                <p className="text-sm text-gray-700 mt-2">{feature.desc}</p>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="bg-gradient-to-b from-[#06010a] to-[#260345] text-center py-20 px-8">
        <h2 className="text-3xl md:text-4xl font-bold mb-6">{S.testimonialsTitle}</h2>
        <p className="text-gray-400 max-w-3xl mx-auto mb-12">{S.testimonialsSub}</p>
        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {S.testimonials.map((user, i) => (
            <div key={i} className="bg-white/10 backdrop-blur-md rounded-xl p-6 text-left">
              <div className="flex items-center gap-4 mb-3">
                <img
                  src={user.img}
                  alt={user.name}
                  className="w-12 h-12 rounded-full border-2 border-purple-400"
                />
                <div>
                  <h4 className="font-semibold text-white">{user.name}</h4>
                  <p className="text-sm text-gray-400">{user.badge}</p>
                </div>
              </div>
              <p className="text-gray-300 text-sm">“{user.review}”</p>
              <p className="text-xs text-gray-400 mt-3">{user.role}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ Section */}
      <section className="bg-white text-black py-20 px-8" id="faq">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
          {S.faqsTitle}
        </h2>
        <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-8">
          <div className="md:col-span-2 space-y-4">
            {S.faqs.map((faq) => (
              <div
                key={faq.id}
                className="border rounded-xl p-4 shadow-sm cursor-pointer"
                onClick={() => setOpenFAQ(openFAQ === faq.id ? null : faq.id)}
              >
                <div className="flex justify-between items-center">
                  <h3 className="font-semibold">{faq.question}</h3>
                  <ChevronDown
                    className={`w-5 h-5 transition-transform ${
                      openFAQ === faq.id ? "rotate-180" : ""
                    }`}
                  />
                </div>
                {openFAQ === faq.id && (
                  <p className="mt-3 text-gray-600 text-sm">{faq.answer}</p>
                )}
              </div>
            ))}
          </div>

          <div className="bg-gray-50 border rounded-xl p-8 flex flex-col justify-between">
            <div>
              <h3 className="font-semibold text-lg mb-2">{S.faqsRightTitle}</h3>
              <p className="text-gray-600 text-sm mb-6">{S.faqsRightText}</p>
            </div>
            <a
              href={S.faqsMail}
              className="bg-yellow-400 text-black font-semibold px-6 py-2 rounded-lg hover:bg-yellow-500 transition"
            >
              Shoot a Direct Mail
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Desktop20;